import type {
	ModuleFull,
	IrrigationPhaseResponse,
	IrrigationSystemResponse,
} from "./irrigationTypes";
import { Irrigations } from "./irrigationTypes";
import {
	T2Schema,
	type ModuleProps,
	type TranslationKey,
} from "@typedefs/modulesInterfaces";
import { useEffect, useMemo } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useFormikContext } from "formik";
import { FEModules } from "@utils/moduleList";
import {
	useCreateIrrigationSystemMutation,
	useDeleteIrrigationSystemMutation,
	useDeleteIrrigationPhaseMutation,
	useCreateIrrigationPhaseMutation,
	useContextAwareGetIrrigationSystem,
	useContextAwareGetIrrigationPhase,
} from "@hooks/index";
import { mergeLocalAndServerChanges } from "../inputs/utils";
import { useIrrigationsContext } from "@contexts/IrrigationsProvider";
import { isEqual } from 'lodash';
import { useVCEnergyHandler } from "@utils/useVCEnergyHandler";
import GenericModule from "../GenericModule";
import GenericOptionalSubModule from "../GenericOptionalSubmodule";


interface IrrigationProps {
	initValues: ModuleFull;
	t2Schema: T2Schema;
	setInitValues: React.Dispatch<React.SetStateAction<ModuleFull>>;
}

const MAX_ENTRIES = 10;

// Configuration object to reduce code duplication
const IRRIGATION_CONFIG = {
	[Irrigations.IrrigationSystem]: {
		fieldName: "irrigations" as const,
		payloadType: "IrrigationSystemCreatePayload" as const,
	},
	[Irrigations.OperationPhase]: {
		fieldName: "irrigationPhases" as const,
		payloadType: "IrrigationPhaseCreatePayload" as const,
	},
} as const;

const IrrigationTierOne = (
	{
		initValues,
		t2Schema,
		beModuleType,
		setInitValues,
		...moduleProps
	}: ModuleProps & IrrigationProps,
) => {
	const moduleContext = useIrrigationsContext();
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { isReadOnly } = usePermissions()

	const { values } = useFormikContext<ModuleFull>();

	const parentId = useMemo(() => moduleList.find((m) => m.id === FEModules.Irrigation)?.uniqueId, [moduleList]);

	//delete functions
	const [deleteIrrigationSystem, { isLoading: isSystemDelLoading }] =
		useDeleteIrrigationSystemMutation();
	const [deleteOperationPhase, { isLoading: isOperationDelLoading }] =
		useDeleteIrrigationPhaseMutation();
	useVCEnergyHandler<ModuleFull, IrrigationPhaseResponse>({ listInputName: "irrigationPhases", energyTypeInputName: "fuel_type" })
	const [addSystem] = useCreateIrrigationSystemMutation();
	const [addPhase] = useCreateIrrigationPhaseMutation();

	// Mutation maps for cleaner access
	const deleteMutations = {
		[Irrigations.IrrigationSystem]: deleteIrrigationSystem,
		[Irrigations.OperationPhase]: deleteOperationPhase,
	};

	const addMutations = {
		[Irrigations.IrrigationSystem]: addSystem,
		[Irrigations.OperationPhase]: addPhase,
	};

	// Generic remove handler
	const createRemoveHandler = (irrigationType: Irrigations) => (
		remove: <T>(index: number) => T | undefined,
		index: number,
		id?: number | null,
	) => {
		if (id) {
			remove(index);
			const deleteMutation = deleteMutations[irrigationType];
			deleteMutation({
				id,
				activityId: activeActivityId ?? 0,
				parent: parentId ?? 0,
			});
		}
	};

	// Generic add handler
	const createAddHandler = (irrigationType: Irrigations) => async () => {
		const config = IRRIGATION_CONFIG[irrigationType];
		const payload = {
			body: { parent: parentId ?? 0 },
			activityId: activeActivityId ?? 0,
		};

		try {
			if (values[config.fieldName].length < MAX_ENTRIES) {
				const addMutation = addMutations[irrigationType];
				await addMutation(payload).unwrap();
			}
		} catch (err) {
			console.error(err);
		}
	};

	// Generic helper functions (aligned with SettlementTierOne)
	const genericDeleteHandler = (name: string) => {
		const irrigationType = name as Irrigations;
		return IRRIGATION_CONFIG[irrigationType] ? createRemoveHandler(irrigationType) : () => { };
	};

	const genericAddHandler = (name: string) => {
		const irrigationType = name as Irrigations;
		return IRRIGATION_CONFIG[irrigationType] ? createAddHandler(irrigationType) : () => { };
	};

	const genericValuesHandler = (name: string) => {
		const config = IRRIGATION_CONFIG[name as Irrigations];
		return config ? initValues[config.fieldName] : null;
	};

	const genericFieldArrayName = (name: string) => {
		const config = IRRIGATION_CONFIG[name as Irrigations];
		return config ? config.fieldName : "";
	};

	const translationKeySubmodule = (name: Irrigations): TranslationKey => {
		return name; // The enum values are already translation keys
	};

	/* ADD SUBMODULES LOGIC  */
	const { data: irrigationSysData, isLoading: isLoadingIrrigationSys } = useContextAwareGetIrrigationSystem({ activityId: activeActivityId ?? 0 }, { refetchOnMountOrArgChange: true, });
	const { data: operationPhasesData, isLoading: isLoadingOperationPhases } = useContextAwareGetIrrigationPhase({ activityId: activeActivityId ?? 0 }, { refetchOnMountOrArgChange: true, });

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const lists = useMemo(
		() => {
			const isIrrigationsUpdated = !isEqual(values.irrigations, irrigationSysData);
			const isPhasesUpdated = !isEqual(values.irrigationPhases, operationPhasesData);

			return {
				irrigations: isIrrigationsUpdated
					? mergeLocalAndServerChanges({
						currentChangesList: values.irrigations,
						incomingList: irrigationSysData ?? [],
					})
					: values.irrigations,
				phases: isPhasesUpdated
					? mergeLocalAndServerChanges({
						currentChangesList: values.irrigationPhases,
						incomingList: operationPhasesData ?? [],
					})
					: values.irrigationPhases,
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[irrigationSysData, operationPhasesData],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (lists.irrigations || lists.phases)
			setInitValues(() => ({
				...values,
				irrigationPhases: lists.phases,
				irrigations: lists.irrigations,
			}));
		if (moduleContext) {
			if (lists.irrigations) moduleContext.setIrrigationsSystemIds(lists.irrigations.map((i) => i.id) as number[]);
			if (lists.phases) moduleContext.setOperationPhaseIds(lists.phases.map((i) => i.id) as number[]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lists, setInitValues]);

	// Mutation loading states map
	const addLoadingStates = {
		[Irrigations.IrrigationSystem]: false, // Add mutation doesn't expose loading state in the current hooks
		[Irrigations.OperationPhase]: false,
	};

	// Helper functions for loading states
	const getDataLoadingStates = {
		[Irrigations.IrrigationSystem]: isLoadingIrrigationSys,
		[Irrigations.OperationPhase]: isLoadingOperationPhases,
	};

	const getOptionalLoadingState = (subModuleName: string) => {
		const irrigationType = subModuleName as Irrigations;
		return IRRIGATION_CONFIG[irrigationType] ? getDataLoadingStates[irrigationType] : false;
	};

	const getCreateLoadingState = (subModuleName: string) => {
		const irrigationType = subModuleName as Irrigations;
		return IRRIGATION_CONFIG[irrigationType] ? addLoadingStates[irrigationType] : false;
	};

	return (
		<GenericModule
			{...moduleProps}
			t2Schema={t2Schema}
			beModuleType={beModuleType}
			disableOptionals
		>
			{
				({ belongsToLuc, isSWW }) => (
					<>
						{moduleProps.moduleSchema.optionalSubmodules?.map((subModule, index) => {
							return (
								<GenericOptionalSubModule<IrrigationSystemResponse | IrrigationPhaseResponse>
									key={`${subModule.name}-${index}`}
									optionalSchema={subModule}
									values={genericValuesHandler(subModule.name) as any}
									createHandler={genericAddHandler(subModule.name)}
									removeHandler={genericDeleteHandler(subModule.name)}
									fieldArrayName={genericFieldArrayName(subModule.name)}
									addLabel={translationKeySubmodule(subModule.name as Irrigations)}
									belongsToLuc={belongsToLuc}
									isSWW={isSWW}
									isReadOnly={isReadOnly}
									isOptionalLoading={getOptionalLoadingState(subModule.name)}
									isCreateLoading={getCreateLoadingState(subModule.name)}
								/>
							);
						})}
					</>
				)
			}
		</GenericModule>
	);
};

export default IrrigationTierOne;
